import * as act from '@/store/modules.actions';
import { IModulesStorage } from '@/models/modules-store.models';
import { modulesService } from '@/services/modules.service';
import { adminModulesService } from '@/services/admin.modules.service';
import { AppModuleUsageType } from '@/models/enums';
import { EventBus } from '@/helpers/event-bus';

const state: IModulesStorage = {
  moduleTariffs: undefined,
  userModules: undefined,
  isOutdated: false,
  adminModuleTariffs: undefined,
  adminUserModules: undefined
};

const actions = {
  [act.ACT_LOADDATA]({ commit, state: IModulesStorage }, forceReload: boolean = false) {
    if (!state.moduleTariffs || !state.userModules || state.isOutdated || forceReload) {
      return Promise.all([
        modulesService.getModulesTariffs(),
        modulesService.getUserModules(AppModuleUsageType.IndividualsAndOrganisations)
      ])
        .then(([moduleTariffs, userModules]) => {
          commit(act.MUT_DATASAVE, { moduleTariffs, userModules });
        });
    } else {
      return Promise.resolve();
    }
  },
  [act.ACT_LOADADMINDATA]({ commit, state: IModulesStorage }, { clientCode, forceReload }) {
    if (!state.adminModuleTariffs || !state.adminUserModules || forceReload) {
      return Promise.all([
        adminModulesService.getModulesTariffs(clientCode),
        adminModulesService.getUserModules(clientCode, AppModuleUsageType.IndividualsAndOrganisations)
      ])
        .then(([moduleTariffs, userModules]) => {
          commit(act.MUT_ADMINDATASAVE, { moduleTariffs, userModules });
        });
    } else {
      return Promise.resolve();
    }
  }
};

const mutations = {
  [act.MUT_DATASAVE](state: IModulesStorage, { moduleTariffs, userModules }): void {
    state.moduleTariffs = moduleTariffs;
    state.userModules = userModules;
    state.isOutdated = false;
  },
  [act.MUT_ADMINDATASAVE](state: IModulesStorage, { moduleTariffs, userModules }): void {
    state.adminModuleTariffs = moduleTariffs;
    state.adminUserModules = userModules;
  },
  [act.MUT_ADMINDATACLEAR](state: IModulesStorage): void {
    state.adminModuleTariffs = undefined;
    state.adminUserModules = undefined;
  }
};

const getters = {
  [act.GET_MODULESTARIFFS]: (state: IModulesStorage) => (usageType: AppModuleUsageType = AppModuleUsageType.None) => {
    return (state.moduleTariffs || []).filter(getFilterModuleTariffsFn(usageType));
  },
  [act.GET_ADMINMODULESTARIFFS]: (state: IModulesStorage) => (usageType: AppModuleUsageType = AppModuleUsageType.None) => {
    return (state.adminModuleTariffs || []).filter(getFilterModuleTariffsFn(usageType));
  },
  [act.GET_USERMODULES]: (state: IModulesStorage) => (usageType: AppModuleUsageType = AppModuleUsageType.None) => {
    return (state.userModules || []).filter(getFilterUserModulesFn(usageType));
  },
  [act.GET_ADMINUSERMODULES]: (state: IModulesStorage) => (usageType: AppModuleUsageType = AppModuleUsageType.None) => {
    return (state.adminUserModules || []).filter(getFilterUserModulesFn(usageType));
  }
};

function getFilterModuleTariffsFn(usageType: AppModuleUsageType) {
  return [AppModuleUsageType.None, AppModuleUsageType.IndividualsAndOrganisations].includes(usageType)
    ? () => true
    : (t) => t.moduleUsageTypeId === usageType || t.moduleUsageTypeId === AppModuleUsageType.IndividualsAndOrganisations;
}

function getFilterUserModulesFn(usageType: AppModuleUsageType) {
  return [AppModuleUsageType.None, AppModuleUsageType.IndividualsAndOrganisations].includes(usageType)
    ? () => true
    : usageType === AppModuleUsageType.Individuals
      ? (t) => !t.isOrgModuleUser
      : (t) => t.isOrgModuleUser;
}

function markDataAsOutdated() {
  state.isOutdated = true;
}

EventBus.$on('user-logged-out', markDataAsOutdated);
EventBus.$on('user-modules-changed', markDataAsOutdated);

export const modules = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
